import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import TermsAcceptanceModal from './components/TermsAcceptanceModal';
import Events from './pages/Events';
import Members from './pages/Members';
import DinnerClub from './pages/DinnerClub';
import BookClub from './pages/BookClub';
import About from './pages/About';
import Auth from './pages/Auth';
import Application from './pages/Application';
import Admin from './pages/Admin';
import Pending from './pages/Pending';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import './App.css';

function AppContent() {
  const { loading, user, profile, application, needsTermsAcceptance, refreshUserData, isSuspended } = useAuth();

  // Debug logging for terms acceptance
  console.log('AppContent Debug:', { 
    user: !!user, 
    application: !!application, 
    terms_accepted: application?.terms_accepted,
    privacy_accepted: application?.privacy_accepted,
    needsTermsAcceptance,
    isSuspended
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-gold"></div>
      </div>
    );
  }

  // Show suspended screen if user is suspended
  if (user && isSuspended) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Account Suspended</h1>
            <p className="text-gray-400 mb-6">
              Your membership has been suspended. Please contact the club administrator for more information.
            </p>
            <a 
              href="mailto:mau@mausanchez.com" 
              className="inline-block bg-brand-gold text-brand-dark px-6 py-3 rounded-xl font-semibold hover:bg-brand-gold/90 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark flex flex-col">
      <Header />
      {/* Terms Acceptance Modal for existing members who haven't accepted */}
      {needsTermsAcceptance && (
        <TermsAcceptanceModal 
          user={user} 
          onAccept={refreshUserData} 
        />
      )}
      <main className="pt-24 md:pt-16 flex-grow">
        <Routes>
          <Route path="/" element={<Events />} />
          <Route path="/members" element={<Members />} />
          <Route path="/dinner-club" element={<DinnerClub />} />
          <Route path="/book-club" element={<BookClub />} />
          <Route path="/about" element={<About />} />
          <Route path="/auth" element={user ? <Navigate to="/" /> : <Auth />} />
          <Route path="/application" element={<Application />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/pending" element={<Pending />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
